import { create } from 'zustand';

import { Territory } from '@/types/territories/territory.type';

const hasValidGeom = (territory: Territory): boolean =>
  !!territory.geom &&
  Array.isArray(territory.geom.coordinates) &&
  territory.geom.coordinates.length > 0;

const hasValidCentroid = (territory: Territory): boolean =>
  !!territory.centroid &&
  Array.isArray(territory.centroid.coordinates) &&
  territory.centroid.coordinates.length >= 2;

const getGeometryLoadedIds = (territories: Territory[]): Set<number> =>
  new Set(
    territories
      .filter((territory) => hasValidGeom(territory))
      .map((territory) => Number(territory.id))
  );

export interface TerritoryState {
  territories: Territory[] | undefined;
  likedTerritories: Territory[] | undefined;
  mapTerritories: Territory[] | undefined;
  focusedTerritory: Territory | null;
  selectedTerritory: Territory | null;
  geometryLoaded: boolean;
  geometryLoadedIds: Set<number>;
  setFocusedTerritory: (focusedTerritory: Territory | null) => void;
  setSelectedTerritory: (selectedTerritory: Territory | null) => void;
  setTerritories: (territories: Territory[]) => void;
  setLikedTerritories: (likedTerritories: Territory[]) => void;
  setMapTerritories: (mapTerritories: Territory[]) => void;
  mergeMapTerritories: (newTerritories: Territory[]) => void;
  mergeGeometries: (geomMap: Map<number, Territory['geom']>) => void;
}

const useTerritoryStore = create<TerritoryState>((set) => ({
  territories: undefined,
  likedTerritories: undefined,
  mapTerritories: undefined,
  focusedTerritory: null,
  selectedTerritory: null,
  geometryLoaded: false,
  geometryLoadedIds: new Set<number>(),
  setFocusedTerritory: (focusedTerritory: Territory | null) =>
    set(() => ({ focusedTerritory })),
  setSelectedTerritory: (selectedTerritory: Territory | null) =>
    set(() => ({ selectedTerritory })),
  setTerritories: (territories: Territory[]) => set(() => ({ territories })),
  setLikedTerritories: (likedTerritories: Territory[]) =>
    set(() => ({ likedTerritories })),
  setMapTerritories: (mapTerritories: Territory[]) =>
    set(() => ({
      mapTerritories,
      geometryLoaded: false,
      geometryLoadedIds: getGeometryLoadedIds(mapTerritories),
    })),
  mergeMapTerritories: (newTerritories: Territory[]) =>
    set((state) => {
      if (!state.mapTerritories) {
        return {
          mapTerritories: newTerritories,
          geometryLoaded: false,
          geometryLoadedIds: getGeometryLoadedIds(newTerritories),
        };
      }

      const mergedMap = new Map(
        state.mapTerritories.map((territory) => [
          Number(territory.id),
          territory,
        ])
      );

      newTerritories.forEach((territory) => {
        const territoryId = Number(territory.id);
        const existingTerritory = mergedMap.get(territoryId);

        mergedMap.set(territoryId, {
          ...existingTerritory,
          ...territory,
          centroid: hasValidCentroid(territory)
            ? territory.centroid
            : (existingTerritory?.centroid ?? territory.centroid),
          geom: hasValidGeom(territory)
            ? territory.geom
            : (existingTerritory?.geom ?? territory.geom),
        });
      });

      const merged = Array.from(mergedMap.values());

      return {
        mapTerritories: merged,
        geometryLoaded: false,
        geometryLoadedIds: getGeometryLoadedIds(merged),
      };
    }),
  mergeGeometries: (geomMap) =>
    set((state) => {
      if (!state.mapTerritories) return state;

      const newIds = new Set(state.geometryLoadedIds);
      for (const key of geomMap.keys()) {
        newIds.add(Number(key));
      }

      return {
        mapTerritories: state.mapTerritories.map((t) => {
          const geom = geomMap.get(Number(t.id));
          return geom ? { ...t, geom } : t;
        }),
        geometryLoaded: true,
        geometryLoadedIds: newIds,
      };
    }),
}));

export { useTerritoryStore };
